import { recordIdentifierFor } from '@warp-drive/core';
import {
  createRecord as createRecordRequest,
  deleteRecord as deleteRecordRequest,
  findRecord as findRecordRequest,
  serializePatch,
  serializeResources,
  updateRecord as updateRecordRequest,
} from '@warp-drive/utilities/json-api';
import { buildBaseURL } from '@warp-drive/utilities';
import { pluralize } from '@warp-drive/utilities/string';
import { SkipCache } from '@warp-drive/core-types/request';
import { warn } from '@ember/debug';
import type { Store } from '@warp-drive/core';
import type {
  ConstrainedRequestOptions,
  FindRecordOptions,
  RequestInfo,
} from '@warp-drive/core/types/request';

/**
 * Save helpers for the WarpDrive mutation path (Phase 2.7) — replace
 * `record.save()` / `record.destroyRecord()` while records stay the form
 * state. The json-api mutation builders intentionally ship WITHOUT a body
 * (the CacheHandler only marks `willCommit`); the caller serializes cache
 * state — these helpers do exactly that, in one place.
 *
 * Each helper accepts an optional trailing `options` argument
 * ({@link ConstrainedRequestOptions}) forwarded to the underlying builder —
 * e.g. `{ namespace: 'api/v2' }` for model types served outside the default
 * namespace.
 *
 * Wire format (types pluralized; keys per the app's negotiated
 * x-json-api-key-format) is applied centrally by
 * TioTypeNormalizationHandler — bodies here are schema-format.
 */

type AnyRecord = object;

/**
 * `JSON.stringify` replacer that THROWS on values it would otherwise silently
 * drop. `JSON.stringify` omits nested `function`/`symbol` values without error,
 * which would let `sanitizeAttrIfUnclonable` ship a lossy snapshot and "succeed"
 * with silent data loss. Throwing routes them through the same path as any other
 * unrepresentable value (leave the attribute untouched + warn in DEBUG) rather
 * than dropping data. BigInt and circular references already make `JSON.stringify`
 * throw natively.
 */
function throwOnUnserializableJsonValue(_key: string, value: unknown): unknown {
  const type = typeof value;
  if (type === 'function' || type === 'symbol') {
    throw new TypeError(`save-record: a ${type} value cannot be serialized as a JSON attribute`);
  }
  return value;
}

/**
 * Replace a single cached attribute with a plain JSON snapshot IF — and only
 * if — its current value can't be `structuredClone`d.
 *
 * WarpDrive's json-api serializers deep-clone attribute values with
 * `structuredClone` (`serializePatch` clones each changed attribute;
 * `serializeResources` clones the whole peeked resource). `structuredClone`
 * throws `DataCloneError` ("#<Object> could not be cloned") on a Proxy, and the
 * TAS forms hold their editing state as Glimmer reactive proxies
 * (`trackedObject` / `trackedArray` from `@ember/reactive/collections`), then
 * assign them straight onto a model's JSON attribute — e.g.
 * `application.fields = this.applicationFields`. The legacy ember-data
 * serializer read through those proxies transparently; under WarpDrive the save
 * crashes.
 *
 * A Proxy can't be `structuredClone`d, but these attributes are plain JSON data
 * that is `JSON.stringify`d onto the wire anyway, so for any attribute the clone
 * rejects we swap the cached value for a JSON round-tripped plain copy, which
 * strips proxies at every depth. The probe is a full `structuredClone`, not a
 * no-op — an *object* attribute that survives is therefore deep-cloned twice
 * (once here, once again in the serializer). Replacement only happens on a
 * `DataCloneError`, so already-cloneable values are read, probed, and left
 * untouched — no behavior change for the common case. The replacement keeps the
 * attribute dirty: `setAttr` diffs against the remote/inflight baseline, not the
 * current local value, so a structurally-equal plain copy still serializes into
 * the patch/resource body.
 *
 * Returns `true` when the value was already cloneable (or a primitive/null),
 * `false` when it was unclonable AND couldn't even be JSON round-tripped (a
 * function, BigInt, or circular reference — none of which belong in a JSON
 * attribute); callers use that only to phrase a DEBUG warning.
 */
function sanitizeAttrIfUnclonable(
  store: Store,
  identifier: ReturnType<typeof recordIdentifierFor>,
  key: string,
  value: unknown
): boolean {
  // Only objects can be unclonable here; primitives/null always clone.
  if (value === null || typeof value !== 'object') {
    return true;
  }
  try {
    structuredClone(value);
    return true;
  } catch (cloneError) {
    // Only a DataCloneError means "not structured-cloneable" — the expected
    // Glimmer reactive-proxy case (trackedObject / trackedArray). Any other
    // error is an unrelated bug we must not mask by JSON-snapshotting, so
    // rethrow it.
    if (!(cloneError instanceof Error) || cloneError.name !== 'DataCloneError') {
      throw cloneError;
    }
    // Swap the proxy for a plain JSON snapshot. Guard the round-trip itself: if
    // even JSON can't represent the value, leave it untouched and let the caller
    // warn in DEBUG so the real culprit is named, rather than trading the
    // DataCloneError for an opaque JSON.stringify crash deeper in serialization.
    try {
      store.cache.setAttr(
        identifier,
        key,
        JSON.parse(JSON.stringify(value, throwOnUnserializableJsonValue)) as never
      );
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Emit the DEBUG-only "attribute isn't serializable" warning. `warn` is stripped
 * in production builds, so this adds no shipping overhead.
 */
function warnUnserializableAttr(
  identifier: ReturnType<typeof recordIdentifierFor>,
  key: string
): void {
  warn(
    `save-record: attribute "${key}" on "${identifier.type}" is not serializable ` +
      `(a function, BigInt, or circular reference); the save will fail until it ` +
      `holds plain JSON.`,
    false,
    { id: 'tio-data.save-record.unserializable-attr' }
  );
}

/**
 * Make the attributes a `serializePatch` save will clone safe for
 * `structuredClone`. `serializePatch` clones ONLY the changed attributes
 * (`cache.changedAttrs`), so this sanitizes exactly that set — sanitizing more
 * would spuriously dirty unchanged attributes into the sparse PATCH body.
 */
function ensureChangedAttrsCloneable(
  store: Store,
  identifier: ReturnType<typeof recordIdentifierFor>
): void {
  if (!store.cache.hasChangedAttrs(identifier)) {
    return;
  }
  const changes = store.cache.changedAttrs(identifier);
  for (const key of Object.keys(changes)) {
    if (!sanitizeAttrIfUnclonable(store, identifier, key, changes[key]?.[1])) {
      warnUnserializableAttr(identifier, key);
    }
  }
}

/**
 * Make EVERY attribute a `serializeResources` save will clone safe for
 * `structuredClone`. Unlike `serializePatch`, `serializeResources` clones the
 * WHOLE peeked resource — every attribute, not just changed ones — so a
 * `changedAttrs`-only probe misses a proxy that lives in an attribute the cache
 * doesn't treat as changed (assigned by-reference equal to the baseline, mutated
 * in place, or default-filled). That is the gap behind the residual TAS guided
 * `DataCloneError` (#9054 / #9023 Pattern 2b): a new-record save serializes
 * through `serializeResources`, whose full-resource clone hits the escaping
 * proxy before the per-changed-attr net ever ran.
 *
 * We can't read `cache.peek(identifier)` to find the offenders — `peek` itself
 * `structuredClone`s the merged attributes and would throw on the very proxy
 * we're hunting. Instead we walk the resource's attribute fields from the schema
 * and read each value with `cache.getAttr`, which returns the LIVE cached value
 * with no clone, then sanitize any that can't be cloned. Relationship / identity
 * / derived fields are skipped — they aren't part of the serialized attributes
 * and `getAttr` is only valid for attribute-kind fields.
 */
function ensureAllAttrsCloneable(
  store: Store,
  identifier: ReturnType<typeof recordIdentifierFor>
): void {
  const fields = store.schema.fields(identifier);
  for (const field of fields.values()) {
    // The cache stores these kinds as JSON attributes (what serializeResources
    // clones); everything else is a relationship, identity, derived, or local
    // field that getAttr does not serve and the serializer does not clone.
    if (
      field.kind !== 'attribute' &&
      field.kind !== 'field' &&
      field.kind !== 'object' &&
      field.kind !== 'array' &&
      field.kind !== 'schema-object' &&
      field.kind !== 'schema-array'
    ) {
      continue;
    }
    const value = store.cache.getAttr(identifier, field.name);
    if (!sanitizeAttrIfUnclonable(store, identifier, field.name, value)) {
      warnUnserializableAttr(identifier, field.name);
    }
  }
}

export function saveNewRecord<T extends AnyRecord>(
  store: Store,
  record: T,
  options?: ConstrainedRequestOptions
) {
  const identifier = recordIdentifierFor(record);
  // serializeResources clones the WHOLE resource, so sanitize every attribute.
  ensureAllAttrsCloneable(store, identifier);
  const init = createRecordRequest(record as Parameters<typeof createRecordRequest>[0], options);
  init.body = JSON.stringify(serializeResources(store.cache, identifier));
  return store.request(init);
}

/**
 * Save a NEW record with a changelog string injected as `_changelog` inside
 * `data.attributes`. Mirrors the legacy
 * `record.save({ adapterOptions: { changelog } })` on a NEW record handled by
 * ApplicationSerializer (Phase 2.7) — the new-record analogue of
 * {@link saveExistingRecordWithChangelog}. `_changelog` is a write-only
 * PaperTrail attribute (not a model attribute), so `saveNewRecord` would drop
 * it; this helper serializes the created resource exactly as `saveNewRecord`
 * does, then adds `_changelog` to the body's attributes before POSTing.
 */
export function saveNewRecordWithChangelog<T extends AnyRecord>(
  store: Store,
  record: T,
  changelog: string,
  options?: ConstrainedRequestOptions
) {
  const identifier = recordIdentifierFor(record);
  // serializeResources clones the WHOLE resource, so sanitize every attribute.
  ensureAllAttrsCloneable(store, identifier);
  const init = createRecordRequest(record as Parameters<typeof createRecordRequest>[0], options);
  const body = serializeResources(store.cache, identifier);
  // Inject the changelog into the serialized body exactly as ApplicationSerializer did.
  const data = body.data as {
    type: string;
    id: string | null;
    attributes?: Record<string, unknown>;
  };
  data.attributes = data.attributes ?? {};
  data.attributes['_changelog'] = changelog;
  init.body = JSON.stringify(body);
  return store.request(init);
}

/**
 * Strip hasMany relationships from a serialized PATCH body, restoring legacy
 * wire parity (#9726).
 *
 * `serializePatch` emits EVERY locally-changed relationship — including a
 * parent's hasMany dirtied as a side effect of child mutations (e.g.
 * `deleteExistingRecord` on a course dirties the owning application's
 * `tasCourses` via the inverse). The legacy `JSONAPISerializer` never
 * serialized manyToOne hasMany relationships into save payloads
 * (`shouldSerializeHasMany` default), so update bodies carried attributes +
 * belongsTo linkage only. Server-side, JSONAPI::Resources treats a hasMany
 * array in a PATCH as an authoritative to-many REPLACEMENT and Rails then
 * unlinks the absent children — a 500 on NOT NULL FKs, silent orphaning on
 * nullable ones (tuitionio/tio-api#9089). Collections are managed through
 * their own endpoints, or explicitly via the relationship builders below —
 * never as a side effect of saving the owning record.
 */
function stripHasManyRelationships(
  store: Store,
  identifier: ReturnType<typeof recordIdentifierFor>,
  patch: ReturnType<typeof serializePatch>
): void {
  const data = patch.data as { relationships?: Record<string, unknown> };
  const relationships = data.relationships;
  if (!relationships) {
    return;
  }
  const fields = store.schema.fields(identifier);
  for (const key of Object.keys(relationships)) {
    const kind = fields.get(key)?.kind;
    // 'hasMany' is the LegacyMode collection kind; 'collection' is its
    // PolarisMode equivalent. to-one kinds (belongsTo/resource) stay.
    if (kind === 'hasMany' || kind === 'collection') {
      delete relationships[key];
    }
  }
  if (Object.keys(relationships).length === 0) {
    delete data.relationships;
  }
}

export function saveExistingRecord<T extends AnyRecord>(
  store: Store,
  record: T,
  options?: ConstrainedRequestOptions
) {
  const identifier = recordIdentifierFor(record);
  ensureChangedAttrsCloneable(store, identifier);
  // `patch: true`: the builder defaults to PUT, but the legacy
  // JSONAPIAdapter#updateRecord always sent PATCH — and the body here is a
  // sparse `serializePatch` document, which is PATCH semantics per JSON:API.
  const init = updateRecordRequest(record as Parameters<typeof updateRecordRequest>[0], {
    ...options,
    patch: true,
  });
  const patch = serializePatch(store.cache, identifier);
  stripHasManyRelationships(store, identifier, patch);
  init.body = JSON.stringify(patch);
  return store.request(init);
}

/**
 * Save an existing record with a changelog string injected as `_changelog` inside
 * `data.attributes`. Mirrors the legacy `record.save({ adapterOptions: { changelog } })`
 * behaviour handled by ApplicationSerializer (Phase 2.7).
 */
export function saveExistingRecordWithChangelog<T extends AnyRecord>(
  store: Store,
  record: T,
  changelog: string,
  options?: ConstrainedRequestOptions
) {
  const identifier = recordIdentifierFor(record);
  ensureChangedAttrsCloneable(store, identifier);
  // PATCH, not the builder's PUT default — see saveExistingRecord.
  const init = updateRecordRequest(record as Parameters<typeof updateRecordRequest>[0], {
    ...options,
    patch: true,
  });
  const patch = serializePatch(store.cache, identifier);
  stripHasManyRelationships(store, identifier, patch);
  // Inject the changelog into the serialized body exactly as ApplicationSerializer did.
  const data = patch.data as {
    type: string;
    id: string | null;
    attributes?: Record<string, unknown>;
  };
  data.attributes = data.attributes ?? {};
  data.attributes['_changelog'] = changelog;
  init.body = JSON.stringify(patch);
  return store.request(init);
}

export function saveRecord<T extends AnyRecord>(
  store: Store,
  record: T,
  options?: ConstrainedRequestOptions
) {
  const isNew = (record as { isNew?: boolean }).isNew;
  return isNew ? saveNewRecord(store, record, options) : saveExistingRecord(store, record, options);
}

export function deleteExistingRecord<T extends AnyRecord>(
  store: Store,
  record: T,
  options?: ConstrainedRequestOptions
) {
  return store.request(
    deleteRecordRequest(record as Parameters<typeof deleteRecordRequest>[0], options)
  );
}

/**
 * Refetch a single existing record and update its cached state, mirroring the
 * legacy `record.reload()` (Phase 2.7). Derives `{ type, id }` from the
 * record's identifier and issues a `findRecord` GET with `reload: true` so the
 * CachePolicy is bypassed and the network is always hit — exactly as
 * `record.reload()` did. The response is put into the cache (updating the
 * already-materialized record in place), and the reloaded record
 * (`content.data`) is returned.
 *
 * Wire parity: this is the SAME GET `record.reload()` sent on main; we keep it
 * (via the builder) rather than dropping it.
 */
export function reloadRecord<T extends AnyRecord>(
  store: Store,
  record: T,
  // `FindRecordOptions` (not `ConstrainedRequestOptions`) so callers can pass
  // `include` — the legacy `record.reload({ include })` refetched related
  // records, and `findRecordRequest` accepts `include` via `FindRecordOptions`.
  options?: FindRecordOptions
): Promise<T> {
  const { type, id } = recordIdentifierFor(record);
  // `findRecord` requires a concrete id; an unsaved record cannot be reloaded,
  // matching legacy `record.reload()` (which GETs the record's own URL).
  const init = findRecordRequest(type, id as string, { ...options, reload: true });
  return store.request(init).then((result) => (result.content as { data: T }).data);
}

/**
 * Persist the deletion of an existing record and remove it from the cache,
 * mirroring the legacy `record.destroyRecord()` (Phase 2.7). Issues the DELETE
 * via the `deleteRecord` builder; the builder tags the request `op:
 * 'deleteRecord'`, which drives the CacheHandler's willCommit/didCommit pair to
 * mark the record deleted and tear down its cache entry on success — the same
 * end state `record.destroyRecord()` produced (delete + unload). No prior
 * `store.deleteRecord()` mark-step is needed: the `deleteRecord` op alone
 * triggers cache removal.
 *
 * Wire parity: byte-identical DELETE to the record's own URL, as on main.
 */
export function destroyRecordViaBuilder<T extends AnyRecord>(
  store: Store,
  record: T,
  options?: ConstrainedRequestOptions
): Promise<void> {
  const init = deleteRecordRequest(record as Parameters<typeof deleteRecordRequest>[0], options);
  return store.request(init).then(() => undefined);
}

/**
 * Add one or more records to a to-many relationship on `record`, mirroring the
 * legacy `record.save({ adapterOptions: { createHasMany: [...] } })` (Phase
 * 2.7) handled by ApplicationAdapter#updateRecord + ApplicationSerializer.
 *
 * Wire parity with the legacy adapter:
 *   - URL: the record's update-record URL + `/relationships/<pluralized related
 *     type>` — exactly `ApplicationAdapter#urlForUpdateRecord`'s
 *     `${originalEndpoint}/relationships/${pluralize(hasManyModel)}`. The
 *     `updateRecord` builder supplies `originalEndpoint` (it pluralizes the
 *     owner type, e.g. `/plans/:id`), to which the relationship segment is
 *     appended.
 *   - Method: POST (the legacy adapter sent `this.ajax(url, 'POST', ...)` for
 *     `createHasMany`).
 *   - Body: `{ data: [{ type, id }, ...] }` — the JSON:API resource-linkage
 *     array ApplicationSerializer#serialize produced
 *     (`{ type: pluralize(modelName), id }` per related record). The `type` is
 *     emitted ALREADY pluralized here because TioTypeNormalizationHandler only
 *     rewrites bodies whose top-level `data` is a resource OBJECT — a linkage
 *     ARRAY body passes through untouched — so this byte-matches the adapter.
 *
 * `relatedRecords` must be non-empty and homogeneous (one related type), as the
 * legacy serializer keyed the whole array off the first record's model name.
 */
export function addRelatedRecordsRequest<T extends AnyRecord, R extends AnyRecord>(
  store: Store,
  record: T,
  relatedRecords: R[],
  options?: ConstrainedRequestOptions
) {
  const first = relatedRecords[0];
  if (!first) {
    throw new Error('addRelatedRecordsRequest requires at least one related record');
  }
  const relatedType = pluralize(recordIdentifierFor(first).type);
  const owner = recordIdentifierFor(record);
  // Derive the (pluralized) owner URL via the `updateRecord` URL builder, then
  // append the relationship segment exactly as the legacy adapter did
  // (`${updateUrl}/relationships/${pluralize(relatedModelName)}`).
  const ownerUrl = buildBaseURL({
    op: 'updateRecord',
    identifier: { type: owner.type, id: owner.id as string },
    ...options,
  });
  // A bare RequestInfo (NOT the `updateRecord` builder's init): this is a
  // relationship-endpoint POST, not a commit of the owner resource, so it must
  // not carry the `op: 'updateRecord'` that would drive the owner through the
  // CacheHandler's commit lifecycle. Like the other action builders, it skips
  // the cache and returns the API's (empty) response unchanged.
  const init: RequestInfo = {
    url: `${ownerUrl}/relationships/${relatedType}`,
    method: 'POST',
    body: JSON.stringify({
      data: relatedRecords.map((related) => ({
        type: relatedType,
        id: recordIdentifierFor(related).id,
      })),
    }),
    cacheOptions: { [SkipCache]: true },
  };
  return store.request(init);
}

/**
 * Set the related record on a to-ONE (belongsTo) relationship of `record`,
 * mirroring the legacy
 * `record.save({ adapterOptions: { updateRelationship: '<relationName>' } })`
 * (wave 3.2c) handled by ApplicationAdapter#urlForUpdateRecord +
 * ApplicationSerializer. This is the to-ONE analogue of
 * {@link addRelatedRecordsRequest} (which is to-MANY: POST + linkage ARRAY).
 *
 * Wire parity with the legacy adapter + serializer:
 *   - URL: the record's update-record URL + `/relationships/<relationName>` —
 *     exactly `ApplicationAdapter#urlForUpdateRecord`'s
 *     `${originalEndpoint}/relationships/${adapterOptions.updateRelationship}`.
 *     The relation name is used VERBATIM in the URL (NOT pluralized) — e.g.
 *     `logo` stays `logo`: `/companies/:id/relationships/logo`,
 *     `/financial-institutions/:id/relationships/logo`. The `updateRecord` URL
 *     builder supplies `originalEndpoint` (it pluralizes the OWNER type, e.g.
 *     `/companies/:id`).
 *   - Method: PATCH. The legacy mutation went through
 *     `JSONAPIAdapter#updateRecord` (ApplicationAdapter#updateRecord falls through
 *     to `super` for the `updateRelationship` case — only the createHasMany /
 *     destroyHasMany branches override the verb), which sends PATCH.
 *   - Body: a SINGLE resource-linkage object
 *     `{ data: { type: pluralize(relatedType), id } }` — exactly what
 *     ApplicationSerializer#serialize emitted for the `updateRelationship` branch:
 *     `{ data: { type: pluralize(relationshipFor(name).type), id } }`. The schema
 *     relationship target type equals the related record's own type (the call
 *     site assigns `model.logo = asset`, and `logo` is `belongsTo('public-asset')`
 *     on both company + financial-institution → both resolve to `public-asset` →
 *     `public-assets`), so deriving the type from
 *     `recordIdentifierFor(relatedRecord).type` matches the serializer. We emit
 *     the SCHEMA-format type (`public-assets`); since the to-ONE body's top-level
 *     `data` is a resource OBJECT (carries a `type`),
 *     `TioTypeNormalizationHandler#serializeBodyForWire` rewrites it to the
 *     NEGOTIATED wire format on the way out — `wireType` =
 *     `pluralize(camelize(type))` under camelized negotiation (→ `publicAssets`),
 *     `pluralize(dasherize(type))` without (→ `public-assets`) — the SAME outbound
 *     type rewriting every mutation builder relies on, and the format the API
 *     expects. (This is the to-ONE/to-MANY DIVERGENCE: {@link
 *     addRelatedRecordsRequest}'s `{ data: [...] }` linkage ARRAY has NO top-level
 *     `type`, so the handler passes it through untouched and that builder
 *     pre-pluralizes the array elements' types itself; the to-ONE OBJECT body is
 *     normalized by the handler instead.)
 *
 * @param record         the owner record carrying the relationship
 * @param relationName   the belongsTo relationship name (e.g. `'logo'`), used
 *                       verbatim in the URL segment
 * @param relatedRecord  the record to link (its `{ type, id }` form the linkage)
 */
export function setRelatedRecordRequest<T extends AnyRecord, R extends AnyRecord>(
  store: Store,
  record: T,
  relationName: string,
  relatedRecord: R,
  options?: ConstrainedRequestOptions
) {
  const owner = recordIdentifierFor(record);
  const relatedType = pluralize(recordIdentifierFor(relatedRecord).type);
  // Derive the PLURALIZED owner URL, then append the relationship segment with
  // the VERBATIM relation name exactly as ApplicationAdapter#urlForUpdateRecord
  // did (`${originalEndpoint}/relationships/${adapterOptions.updateRelationship}`,
  // where `originalEndpoint` is the legacy `buildURL`'s PLURAL owner path, e.g.
  // `/companies/9`, `/financial-institutions/9`).
  //
  // NB: `buildBaseURL` does NOT pluralize on its own — when no `resourcePath` is
  // given it falls back to the RAW `identifier.type` (see
  // @warp-drive/utilities `resourcePathForType`, which returns `identifier.type`
  // verbatim). Only the `updateRecord()` / `createRecord()` builder WRAPPERS set
  // `resourcePath: pluralize(camelize(type))`. We replicate the dasherized-plural
  // owner path (camelize would WRONGLY camelCase `financial-institution`) by
  // setting `resourcePath` to `pluralize(owner.type)` — `owner.type` is already
  // the dasherized schema type, and `pluralize` alone preserves the dashes
  // (`financial-institution` -> `financial-institutions`).
  const ownerUrl = buildBaseURL({
    op: 'updateRecord',
    identifier: { type: owner.type, id: owner.id as string },
    resourcePath: pluralize(owner.type),
    ...options,
  });
  // A bare RequestInfo (NOT the `updateRecord` builder's init): this is a
  // relationship-endpoint PATCH, not a commit of the owner resource, so it must
  // not carry `op: 'updateRecord'` (which would drive the owner through the
  // CacheHandler's commit lifecycle). It skips the cache and returns the API's
  // (empty) response unchanged.
  const init: RequestInfo = {
    url: `${ownerUrl}/relationships/${relationName}`,
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        type: relatedType,
        id: recordIdentifierFor(relatedRecord).id,
      },
    }),
    cacheOptions: { [SkipCache]: true },
  };
  return store.request(init);
}

/**
 * Replace the full set of records on a to-MANY (hasMany) relationship by
 * sending PATCH to `/<owner-plural>/:id/relationships/<relationshipName>`.
 * Sending an empty array clears the relationship entirely.
 *
 * This is the to-MANY analogue of {@link setRelatedRecordRequest} (to-ONE).
 * Unlike {@link addRelatedRecordsRequest} (POST, appends), this REPLACES
 * the entire set in one request.
 *
 * The `relationshipName` is used VERBATIM in the URL path (NOT derived from
 * the related type). This is necessary when the relationship name on the
 * owner differs from the related resource's type name.
 *
 * Wire format: `{ data: [{ type: pluralize(relatedType), id }, ...] }`.
 * Array linkage bodies are NOT rewritten by `TioTypeNormalizationHandler`
 * (only top-level resource objects are), so types are pre-pluralised here,
 * matching the pattern in {@link addRelatedRecordsRequest}.
 *
 * @param record           the owner record carrying the relationship
 * @param relationshipName the hasMany relationship name used verbatim in the URL
 * @param relatedRecords   the FULL desired set; pass `[]` to clear
 */
export function replaceRelatedRecordsRequest<T extends AnyRecord, R extends AnyRecord>(
  store: Store,
  record: T,
  relationshipName: string,
  relatedRecords: R[],
  options?: ConstrainedRequestOptions
) {
  const owner = recordIdentifierFor(record);
  const ownerUrl = buildBaseURL({
    op: 'updateRecord',
    identifier: { type: owner.type, id: owner.id as string },
    resourcePath: pluralize(owner.type),
    ...options,
  });
  const init: RequestInfo = {
    url: `${ownerUrl}/relationships/${relationshipName}`,
    method: 'PATCH',
    body: JSON.stringify({
      data: relatedRecords.map((related) => {
        const { type, id } = recordIdentifierFor(related);
        if (!id)
          throw new Error(
            `replaceRelatedRecordsRequest: unsaved record (type '${type}') has no id`
          );
        return { type: pluralize(type), id };
      }),
    }),
    cacheOptions: { [SkipCache]: true },
  };
  return store.request(init);
}
